/* eslint-disable no-unused-vars */
// _________________________API RESPONSE TYPES_________________________

export type ApiPupilScreener = {
  id: number
  class_id: number
  pupil_id: number
  teacher_id: number
  teacher_answers?: any
  pupil_answers: PupilAnswer[]
  teacher_questions_completed: boolean
  pupil_questions_completed: boolean
  teacher_notes?: null
  code: number
  code_expiry: string
  created_at: string
  completed_at?: null
  teacher_questions_answered_by: number
  screener_template_id: number
  pupil_name: string
  full_name: string
  pupil_questions_template?: ApiSubtestEntity[] | null
  teacher_questions_template?: any
  metadata: null | { [key: string]: any }
  school_name: string
  screener_template_name: string
  extended_answer_time?: boolean
}

export type ApiAnswerOrOptionsEntity = {
  index: number
  label: string
  value: number
}

export type ApiSubtestEntity = {
  id: number
  key: string
  index: number
  title: string
  archived: boolean
  intro_url: string
  outro_url: string
  questions?: ApiQuestionEntity[] | null
  answer_type: string
  question_type: string
  reporting_group: string
  practice_questions?: ApiQuestionEntity[] | null
  screener_template_id: number
}
export type ApiQuestionEntity = {
  id: number
  index: number
  title: string
  target?: string | null
  options?: ApiOptionsEntity[] | null
  archived: boolean
  asset_url?: string | null
  wait_time: number
  prompt_url?: string | null
  description?: string | null
  question_url: string
  intro_url: string
  answer_given_outro_url: string
  placeholder_url: string | null
  alternative_targets?: (string | null)[] | null
  is_practice_question: boolean
  screener_pupil_subtest_id: number
}
export type ApiOptionsEntity = {
  index: number
  title: string
  correct: boolean
  imageUrl: string
}

// _________________________STORE TYPES_________________________

export type PupilScreener = {
  id: number
  classScreenerId: number
  pupilId: number
  teacherId: number
  teacherAnswers?: any
  pupilAnswers: PupilAnswer[]
  teacherQuestionsCompleted: boolean
  pupilQuestionsCompleted: boolean
  teacherNotes?: null
  code: number
  codeExpiry: string
  createdAt: string
  completedAt?: null
  teacherQuestionsAnsweredBy: number
  screenerTemplateId: number
  fullName: string
  pupilQuestionsTemplate: SubtestEntity[]
  teacherQuestionsTemplate?: any
  metadata: null | { [key: string]: any }
  rewardCount: number
  extendedTimeMultiplier?: number
}

export type AnswerOrOptionsEntity = {
  index: number
  label: string
  value: number
}

export type PupilAnswer = {
  practiceAnswer: boolean
  questionId: number
  questionIndex: number
  subtestId: number
  subtestIndex: number
  fileId?: string | null
  selection?: any
}

// Type for "None Word Target" reporting group
export type NoneWordTarget = {
  target: string
  phoneticTarget: string
}

// Type for "Sentence Repetition" reporting group
export type SentenceRepetitionTarget = {
  target: string
}

// Type for "Sentence Comprehension" reporting group
export type SentenceComprehensionTarget = {
  index: number
  title: string
  correct: boolean
  imageUrl: string
}

// Type for "Expressive Verb Morphology" reporting group
export type ExpressiveVerbMorphologyTarget = {
  target: string
}

// Type for "Speech Sound Production" reporting group
export type SpeechSoundProductionTarget = {
  target: string
  phoneticTarget: string
}

// Union type to cover all possible target formats
export type TargetType =
  | NoneWordTarget
  | SentenceRepetitionTarget
  | SentenceComprehensionTarget
  | ExpressiveVerbMorphologyTarget
  | SpeechSoundProductionTarget

export type TranscriptionResult = {
  text: string
  matched?: boolean
  matchedText?: string
}

export type WordOrPhraseMatch = {
  type: 'word' | 'phrase'
  keyWords: string[]
  alternatives?: { [word: string]: string[] }
}

export type reportingGroupKeys =
  | 'nonword'
  | 'sentenceRepetition'
  | 'sentenceComprehension'
  | 'verbMorphology'
  | 'soundProduction'
  | 'wellBeingQuestionnaire'

export type SubtestEntity = {
  id: number
  key: reportingGroupKeys
  index: number
  title: string
  archived: boolean
  introUrl: string
  outroUrl: string
  questions?: QuestionEntity[] | null
  answerType: string
  questionType: string
  reportingGroup: string
  practiceQuestions?: QuestionEntity[] | null
  screenerTemplateId: number
  metadata: null | { [key: string]: any }
}

export type QuestionEntity = {
  id: number
  index: number
  reportingGroup: string
  title: string
  target?: TargetType[] | null
  options?: OptionsEntity[] | null
  archived: boolean
  assetUrl?: string | null
  waitTime: number
  promptUrl?: string | null
  avatarAnswerUrl?: string | null
  description?: string | null
  questionUrl: string
  introUrl: string
  answerGivenOutroUrl: string
  placeholderUrl: string | null
  isPracticeQuestion: boolean
  screenerPupilSubtestId: number
  animationTriggers: AnimationTrigger | null
  completed: Boolean
}

export type OptionsEntity = {
  index: number
  title: string
  correct?: boolean
  imageUrl?: string
  score?: number
}

export type QuestionsArrayPropertyName = 'practiceQuestions' | 'questions'

export interface AudioPlayer extends Howl {
  id: string
  start: (fade?: boolean | number) => void
  end: (fade?: boolean) => void
  remove: () => void
  setVolume: (level: number, fade?: boolean) => void
  connectAnalyzer: (analyser: AnalyserNode) => void
  customVolume: number
}

export type Track = {
  id: string
  name: string
  url: string
  volume: number
  loop: boolean
  howl?: AudioPlayer
  currentTime?: number
  timeTrackingEnabled?: boolean
}

export enum HowlErrorCode {
  LoadFailed = 1,
  DecodeFailed,
  NoSupport
}

export interface AudioLoadError extends Error {
  error: number
  id: string
}

export type ErrorMessage = {
  message: string
  type: 'danger' | 'warning'
  helpLink?: string
  helpText?: string
  metadata?: {
    firewallGuideUrl?: string
    serviceUrl?: string
    [key: string]: any
  }
}

export type progressSateError =
  | 'UserUnresponsive'
  | 'NoAnswersDetected'
  | 'DeepgramUnavailable'
  | 'IncorrectMicInUse'

// _________________________ MISCELLANEOUS _________________________

export type QuestionTimestamp = {
  start: number
  end: number
  word: string
}

export type ViewName =
  | 'ErrorView'
  | 'CodeInputView'
  | 'StartScreenerView'
  | 'ScreenerQuestionsView'
  | 'ScreenerQuestionsViewLoader'
  | 'PausedScreenerView'
  | 'StaggerCountdownView'
  | 'StatusView'

export type ShowView = {
  [key in `show${ViewName}`]: boolean
}

export type Code = {
  [key: number]: string
}
export type AvatarOptions = {
  position: AvatarPosition
  size: AvatarSize
  audio: string
}

export type AvatarPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'center'
  | 'bottom'
  | 'dynamic'
  | 'fixed'
  | 'hidden'
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'height'

export type AvatarWalkingCoordinates = {
  startX?: number
  startY?: number
  targetX?: number
  targetY?: number
  pathPoints: Array<{ x: number; y: number }>
}

export type AvatarPathPoint = {
  x: number
  y: number
  inFlipZone?: boolean
}

export type AvatarWalkingState = {
  isWalking: boolean
  walkingDirection: 'left' | 'right'
  currentPointIndex: number
  reachedDestination: boolean
  currentProgress: number
} & AvatarWalkingCoordinates

export type AvatarAnimationTimeStamps = {
  [key in AvatarAnimation]: AnimationTimeStamp
}

export type AvatarTransitionCallback = {
  during: (() => void) | null
  after: (() => void) | null
}

export type AnimationTimeStamp = {
  start: number
  end: number
  break?: number | null
  animation: string
  audio?: boolean
}

export type AnimationTrigger = {
  start: number
  end: number
  animation: string
}

export type AvatarAnimation = string

export type SceneAnimation = string | null

export type IntroOutroAnimation = string | null

export type IntroOutroAnimationTimeStamps = {
  [key in string]: AnimationTimeStamp
}

export type SceneAnimationReference = {
  name: SceneAnimation
  fullScreen?: boolean
}

export type ActiveChestPosition = { x: number; y: number; width: number }

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'outline-primary'
  | 'outline-secondary'
  | 'outline-success'
  | 'outline-danger'
  | 'outline-warning'
  | 'outline-info'
  | 'outline-light'
  | 'outline-dark'

export type ButtonSize = 'sm' | 'md' | 'lg'

export type InputMethod = 'qr' | 'manual'

type StepFunction = () => void

type StepObject = {
  [key: number]: StepFunction
  promptFunction: StepFunction
}

export type Steps = {
  [key: string]: StepObject
}

export type AssetType = 'image' | 'video'

export type MouthCue = {
  start: number
  end: number
  value: string
}

export type DeviceType = 'IPAD' | 'IPHONE' | 'MOBILE' | 'DESKTOP' | 'TABLET'
export type BrowserType =
  | 'UC_BROWSER'
  | 'EDGE'
  | 'GOOGLE_BOT'
  | 'CHROMIUM'
  | 'FIREFOX'
  | 'IE'
  | 'CHROME'
  | 'SAFARI'
  | 'OPERA'

export type DeviceTypes = {
  [key in DeviceType]: string
}
export type BrowserTypes = {
  [key in BrowserType]: string
}

export type WidgetData = {
  questionsCount?: number
  answersCount?: number
  error?: string
  isCompleted?: boolean
  pupilId?: number
  removeData?: boolean
}

export type DeviceInfo = {
  deviceId: string | null
  kind?: string
  label: string
  groupId?: string | null
}

export type AudioAnalyserVolumeCallbackData = {
  volume: number
  average: number
}

export type AudioAnalyserRecordingCallbackData = {
  inputBlob: Blob
  inputDuration: number
}

export type DeviceKind = 'audioinput' | 'audiooutput' | 'videoinput'

export type ManagedTimeout = {
  clear: () => void
}

export type ManagedInterval = {
  clear: () => void
}

// star pop game

export type GameElement = {
  id: number
  x: number
  y: number
  size: number
  rotationSpeed: number
  velocity: number
  collected: boolean
  score: number
  domElement?: HTMLDivElement
  removeElementTimeoutId?: ReturnType<typeof setTimeout>
}

export type GameConfig = {
  minSize: number
  maxSize: number
  minRotationSpeed: number
  maxRotationSpeed: number
  minVelocity: number
  maxVelocity: number
  minInterval: number
  maxInterval: number
  maxStars: number
  gameLength: number
  gameSounds: { name: string; url: string }[]
}
